import React, { useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Divider,
  Grid,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  Check,
  Filter,
  Layers,
  RotateCcw,
  Target,
  Trophy,
} from 'lucide-react';
import { CCASubdomain, CCA_SUBDOMAINS } from '../../../../questions/CCABank/ccaDummyData';

export type CCAStatus = 'invite-sent' | 'in-progress' | 'completed';

export interface CCAFilters {
  totalScoreMin: number | '';
  totalScoreMax: number | '';
  subdomainScores: Record<CCASubdomain, { min: number | ''; max: number | '' }>;
  topX: number | '';
  statuses: CCAStatus[];
}

interface StatusOption {
  value: CCAStatus;
  label: string;
  selectedColor: { bg: string; text: string; border: string };
}

const STATUS_OPTIONS: StatusOption[] = [
  {
    value: 'invite-sent',
    label: 'Invite Sent',
    selectedColor: { bg: '#eff6ff', text: '#1d4ed8', border: '#93c5fd' },
  },
  {
    value: 'in-progress',
    label: 'In Progress',
    selectedColor: { bg: '#fffbeb', text: '#b45309', border: '#fcd34d' },
  },
  {
    value: 'completed',
    label: 'Completed',
    selectedColor: { bg: '#ecfdf5', text: '#047857', border: '#6ee7b7' },
  },
];

const defaultFilters = (): CCAFilters => ({
  totalScoreMin: '',
  totalScoreMax: '',
  subdomainScores: Object.fromEntries(
    CCA_SUBDOMAINS.map((sd) => [sd, { min: '', max: '' }])
  ) as Record<CCASubdomain, { min: number | ''; max: number | '' }>,
  topX: '',
  statuses: [],
});

export interface CCAFilterDrawerContentProps {
  onApply: () => void;
}

const SUBDOMAIN_ACCENT_COLORS: Record<CCASubdomain, string> = {
  'Abstract Reasoning': '#8b5cf6',
  'Logical Reasoning': '#3b82f6',
  'Quantitative Aptitude': '#10b981',
  'Verbal Ability': '#f59e0b',
  'Data Interpretation': '#f43f5e',
};

export const CCAFilterDrawerContent = ({
  onApply,
}: CCAFilterDrawerContentProps) => {
  const [filters, setFilters] = useState<CCAFilters>(defaultFilters());

  const setTotalMin = (v: string) =>
    setFilters((p) => ({ ...p, totalScoreMin: v === '' ? '' : Number(v) }));
  const setTotalMax = (v: string) =>
    setFilters((p) => ({ ...p, totalScoreMax: v === '' ? '' : Number(v) }));
  const setTopX = (v: string) =>
    setFilters((p) => ({ ...p, topX: v === '' ? '' : Number(v) }));

  const setSubdomainMin = (sd: CCASubdomain, v: string) =>
    setFilters((p) => ({
      ...p,
      subdomainScores: {
        ...p.subdomainScores,
        [sd]: { ...p.subdomainScores[sd], min: v === '' ? '' : Number(v) },
      },
    }));

  const setSubdomainMax = (sd: CCASubdomain, v: string) =>
    setFilters((p) => ({
      ...p,
      subdomainScores: {
        ...p.subdomainScores,
        [sd]: { ...p.subdomainScores[sd], max: v === '' ? '' : Number(v) },
      },
    }));

  const toggleStatus = (s: CCAStatus) =>
    setFilters((p) => ({
      ...p,
      statuses: p.statuses.includes(s)
        ? p.statuses.filter((x) => x !== s)
        : [...p.statuses, s],
    }));

  const handleReset = () => setFilters(defaultFilters());

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
        bgcolor: 'background.paper',
      }}
    >
      {/* Scrollable Mid Content */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          p: 2.5,
          display: 'flex',
          flexDirection: 'column',
          gap: 3,
          '&::-webkit-scrollbar': {
            width: '6px',
          },
          '&::-webkit-scrollbar-thumb': {
            bgcolor: 'rgba(0, 0, 0, 0.15)',
            borderRadius: '3px',
          },
        }}
      >
        {/* Section 1: CCA Assessment Status */}
        <Box>
          <Typography
            variant="caption"
            sx={{
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              color: 'text.secondary',
              display: 'block',
              mb: 1.25,
            }}
          >
            Assessment Status
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {STATUS_OPTIONS.map((status) => {
              const isSelected = filters.statuses.includes(status.value);
              return (
                <Chip
                  key={status.value}
                  label={status.label}
                  clickable
                  onClick={() => toggleStatus(status.value)}
                  icon={isSelected ? <Check size={14} /> : undefined}
                  sx={{
                    fontWeight: 600,
                    fontSize: '12px',
                    height: '32px',
                    borderRadius: '8px',
                    border: '1.5px solid',
                    borderColor: isSelected ? status.selectedColor.border : 'grey.300',
                    bgcolor: isSelected ? status.selectedColor.bg : 'grey.50',
                    color: isSelected ? status.selectedColor.text : 'text.secondary',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      bgcolor: isSelected ? status.selectedColor.bg : 'grey.100',
                    },
                  }}
                />
              );
            })}
          </Stack>
        </Box>

        <Divider />

        {/* Section 2: Top X Candidates */}
        <Box>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
            <Trophy size={16} color="#059669" />
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 700, fontSize: '13px', color: 'text.primary' }}
            >
              Top Candidates by Score
            </Typography>
          </Stack>
          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1.5 }}>
            Show only the top N highest-scoring CCA candidates (e.g. 10)
          </Typography>
          <TextField
            size="small"
            type="number"
            fullWidth
            placeholder="e.g. 10"
            value={filters.topX}
            onChange={(e) => setTopX(e.target.value)}
            inputProps={{ min: 1, max: 100 }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
                fontSize: '13px',
              },
            }}
          />
        </Box>

        <Divider />

        {/* Section 3: Total CCA Score Range */}
        <Box>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
            <Target size={16} color="#2563eb" />
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 700, fontSize: '13px', color: 'text.primary' }}
            >
              Total CCA Score (0 – 100)
            </Typography>
          </Stack>
          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1.5 }}>
            Filter applicants within an overall composite score bracket
          </Typography>
          <Grid container spacing={1.5}>
            <Grid item xs={6}>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, display: 'block', mb: 0.5 }}>
                Minimum Score
              </Typography>
              <TextField
                size="small"
                type="number"
                fullWidth
                placeholder="0"
                value={filters.totalScoreMin}
                onChange={(e) => setTotalMin(e.target.value)}
                inputProps={{ min: 0, max: 100 }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                    fontSize: '13px',
                  },
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, display: 'block', mb: 0.5 }}>
                Maximum Score
              </Typography>
              <TextField
                size="small"
                type="number"
                fullWidth
                placeholder="100"
                value={filters.totalScoreMax}
                onChange={(e) => setTotalMax(e.target.value)}
                inputProps={{ min: 0, max: 100 }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                    fontSize: '13px',
                  },
                }}
              />
            </Grid>
          </Grid>
        </Box>

        <Divider />

        {/* Section 4: Subdomain Scores */}
        <Box>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
            <Layers size={16} color="#7e22ce" />
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 700, fontSize: '13px', color: 'text.primary' }}
            >
              Subdomain Thresholds (0 – 100)
            </Typography>
          </Stack>
          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 2 }}>
            Set minimum and maximum thresholds for each cognitive domain
          </Typography>

          <Stack spacing={2}>
            {CCA_SUBDOMAINS.map((sd) => {
              const accentColor = SUBDOMAIN_ACCENT_COLORS[sd] || '#64748b';

              return (
                <Paper
                  key={sd}
                  elevation={0}
                  sx={{
                    p: 2,
                    borderRadius: '10px',
                    border: '1px solid',
                    borderColor: 'grey.200',
                    borderLeft: `4px solid ${accentColor}`,
                    bgcolor: 'grey.50',
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 600, color: 'text.primary', mb: 1.5 }}
                  >
                    {sd}
                  </Typography>

                  <Grid container spacing={1.5}>
                    <Grid item xs={6}>
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500, display: 'block', mb: 0.5 }}>
                        Min
                      </Typography>
                      <TextField
                        size="small"
                        type="number"
                        fullWidth
                        placeholder="Any"
                        value={filters.subdomainScores[sd].min}
                        onChange={(e) => setSubdomainMin(sd, e.target.value)}
                        inputProps={{ min: 0, max: 100 }}
                        sx={{
                          bgcolor: 'background.paper',
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '6px',
                            fontSize: '12.5px',
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500, display: 'block', mb: 0.5 }}>
                        Max
                      </Typography>
                      <TextField
                        size="small"
                        type="number"
                        fullWidth
                        placeholder="Any"
                        value={filters.subdomainScores[sd].max}
                        onChange={(e) => setSubdomainMax(sd, e.target.value)}
                        inputProps={{ min: 0, max: 100 }}
                        sx={{
                          bgcolor: 'background.paper',
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '6px',
                            fontSize: '12.5px',
                          },
                        }}
                      />
                    </Grid>
                  </Grid>
                </Paper>
              );
            })}
          </Stack>
        </Box>
      </Box>

      {/* Fixed Footer Actions */}
      <Box
        sx={{
          p: 2,
          borderTop: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1.5,
          flexShrink: 0,
        }}
      >
        <Button
          variant="text"
          color="inherit"
          size="small"
          onClick={handleReset}
          startIcon={<RotateCcw size={14} />}
          sx={{
            fontWeight: 600,
            fontSize: '12.5px',
            color: 'text.secondary',
            '&:hover': { color: 'error.main' },
          }}
        >
          Reset All
        </Button>

        <Stack direction="row" spacing={1.5}>
          <Button
            variant="outlined"
            size="small"
            onClick={onApply}
            sx={{
              borderRadius: '8px',
              fontWeight: 600,
              fontSize: '12.5px',
              borderColor: 'grey.300',
              color: 'text.primary',
              textTransform: 'none',
              px: 2,
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            size="small"
            onClick={onApply}
            startIcon={<Filter size={14} />}
            sx={{
              borderRadius: '8px',
              fontWeight: 600,
              fontSize: '12.5px',
              textTransform: 'none',
              bgcolor: 'primary.main',
              px: 2,
            }}
          >
            Apply Filters
          </Button>
        </Stack>
      </Box>
    </Box>
  );
};
